import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@wlb/shared/context/AuthContext';
import { useSurvey } from '@wlb/shared/context/SurveyContext';
import registrationService from '@wlb/shared/api/services/registrationService';
import type { BuddyMatchItem } from '@wlb/shared/types/match';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { AuthFlowLayout } from '../components/layout/AuthFlowLayout';
import { BrandLogo } from '../components/ui/BrandLogo';
import { Button } from '../components/ui/Button';
import {
  hydrateOnboardingMatchUsernames,
  mapOnboardingMatches,
} from '../features/auth/mapOnboardingMatch';
import { OnboardingIllustration } from '../features/auth/OnboardingIllustration';
import { MATCH_ILLUSTRATIONS } from '../features/auth/onboardingIllustrations';
import './matchOnboarding.css';

/** Fixed display time per progress stage (ms). The API call runs in the
 * background; stages play out regardless of how fast it completes. */
const STAGE_DURATION = 1500;

const STAGES = ['matchLoading.stage1', 'matchLoading.stage2', 'matchLoading.stage3', 'matchLoading.stage4'] as const;

type ApiStatus = 'pending' | 'success' | 'error';

export function MatchLoading() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userGUID, completeAuthSession, token } = useAuth();
  const { surveyData } = useSurvey();
  const [stage, setStage] = useState(0);
  const [apiStatus, setApiStatus] = useState<ApiStatus>('pending');
  const [matches, setMatches] = useState<BuddyMatchItem[]>([]);
  const submitted = useRef(false);
  const mounted = useRef(true);
  const navigated = useRef(false);

  // ── Stage progression: each stage shows for STAGE_DURATION, then advances ──
  useEffect(() => {
    if (stage >= STAGES.length - 1) return;
    const timer = window.setTimeout(() => {
      if (mounted.current) setStage((s) => Math.min(s + 1, STAGES.length - 1));
    }, STAGE_DURATION);
    return () => window.clearTimeout(timer);
  }, [stage]);

  // ── Mount tracking ──────────────────────────────────────────────────────────
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // ── Background API call (fires once on mount) ───────────────────────────────
  useEffect(() => {
    if (submitted.current) return;
    submitted.current = true;

    if (!userGUID) {
      setApiStatus('error');
      return;
    }

    registrationService.submitQuestionnaire(surveyData, userGUID)
      .then(response => hydrateOnboardingMatchUsernames(mapOnboardingMatches(response)))
      .then((hydratedMatches) => {
        if (mounted.current) {
          setMatches(hydratedMatches);
          setApiStatus('success');
        }
      })
      .catch(() => {
        if (mounted.current) setApiStatus('error');
      });
  }, [surveyData, userGUID]);

  // ── Navigate to results only after all stages AND API success ────────────────
  useEffect(() => {
    if (navigated.current) return;
    if (apiStatus === 'success' && stage >= STAGES.length - 1) {
      navigated.current = true;
      navigate('/onboarding/matches', { replace: true, state: { matches } });
    }
  }, [apiStatus, stage, navigate, matches]);

  // ── Error state: let the user continue to the app ───────────────────────────
  if (apiStatus === 'error') {
    return (
      <AuthFlowLayout>
        <section className="match-onboarding match-onboarding--error" role="alert" aria-label={t('matchLoading.accessibility.errorAlert')}>
          <div className="match-onboarding__illustration" aria-hidden><BrandLogo compact /></div>
          <h1>{t('matchLoading.errorTitle')}</h1>
          <p>{t('matchLoading.errorMessage')}</p>
          <Button fullWidth size="lg" onClick={async () => {
            navigate('/', { replace: true });
            await completeAuthSession(token ?? undefined);
          }}>{t('common.buttons.continueToApp')}</Button>
        </section>
      </AuthFlowLayout>
    );
  }

  // ── Loading state: staged progress messages ─────────────────────────────────
  return (
    <AuthFlowLayout>
      <section className="match-onboarding" aria-busy="true" aria-label={t('matchOnboarding.loadingMatches')}>
        <button className="auth-flow__back" type="button" onClick={() => navigate('/survey/q9')} aria-label={t('matchOnboarding.backToSurvey')}>‹</button>
        <OnboardingIllustration src={MATCH_ILLUSTRATIONS.loading} size="hero" />
        <div className="match-onboarding__copy" key={STAGES[stage]}>
          <h1>{t(STAGES[stage])}</h1>
          <p>{t('matchLoading.subtitle')}</p>
        </div>
        <div className="match-onboarding__skeleton-list" aria-hidden>
          {Array.from({ length: 4 }, (_, index) => <div className="match-onboarding__skeleton-card" key={index}><i /><span><b /><b /></span><em /></div>)}
        </div>
      </section>
    </AuthFlowLayout>
  );
}
